import React, {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ClickAwayListener,
  MenuList,
  MenuListProps,
  Paper,
  PaperProps,
  Popper,
  PopperPlacementType,
  SxProps,
  Theme,
} from "@mui/material";
import type { MenuProps } from "@mui/material/Menu";

// Shared non-modal popup menus (IF10 G1, staff dashboard UX audit T-1/T-2).
//
// MUI Menu and the TextField `select` menu are modal: they put a full-screen
// layer over the page and lock scrolling. The dashboard scrolls in an inner
// panel ([data-dashboard-scroll], Layout.tsx), so the page froze while any
// menu was open. Claude Design ds-components/InvoiceActionsMenu.jsx is the
// model: a fixed popup with no backdrop that follows its trigger on scroll,
// closes when the trigger leaves the visible area, on an outside click and
// on Esc.

export const DASHBOARD_SCROLL_SELECTOR = "[data-dashboard-scroll]";

// True when the anchor is no longer in the visible part of the dashboard
// scroll panel (or of the window when the anchor is outside that panel).
export const isAnchorOutOfView = (anchorEl: HTMLElement): boolean => {
  if (!anchorEl.isConnected) return true;
  const rect = anchorEl.getBoundingClientRect();
  const scroller = anchorEl.closest<HTMLElement>(DASHBOARD_SCROLL_SELECTOR);
  const view = scroller
    ? scroller.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight };
  return rect.bottom < view.top || rect.top > view.bottom;
};

// Closes an open popup when a scroll or resize moves its anchor out of view.
export const useCloseWhenAnchorLeavesView = (
  anchorEl: HTMLElement | null,
  open: boolean,
  onClose: () => void,
) => {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open || !anchorEl) return undefined;
    const check = () => {
      if (isAnchorOutOfView(anchorEl)) onCloseRef.current();
    };
    window.addEventListener("scroll", check, true);
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check, true);
      window.removeEventListener("resize", check);
    };
  }, [anchorEl, open]);
};

// A popup opened from inside a MUI Dialog/Modal must render inside that
// modal's focus-trap root. Otherwise the modal pulls focus back out of the
// popup. Outside a modal the popup renders in document.body.
export const getModalFocusRoot = (
  anchorEl: HTMLElement | null,
): HTMLElement | undefined => {
  const modalRoot = anchorEl?.closest<HTMLElement>(".MuiModal-root");
  if (!anchorEl || !modalRoot) return undefined;
  return (
    (Array.from(modalRoot.children).find((child) =>
      child.contains(anchorEl),
    ) as HTMLElement | undefined) ?? modalRoot
  );
};

const toSxArray = (sx: SxProps<Theme> | undefined) =>
  sx === undefined ? [] : Array.isArray(sx) ? sx : [sx];

// MUI Menu paper defaults, so a swapped call site keeps its look.
const defaultPaperSx = {
  maxHeight: "calc(100vh - 32px)",
  minWidth: 16,
  outline: 0,
  overflowX: "hidden",
  overflowY: "auto",
};

export type AnchoredMenuPaperProps = Partial<PaperProps> & {
  "data-testid"?: string;
};

export type AnchoredMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  // "menu": children are menu items in a MenuList (arrow keys, Tab closes).
  // "panel": children are form content (date inputs, buttons); Tab moves
  // between the fields.
  variant?: "menu" | "panel";
  placement?: PopperPlacementType;
  // Gap between the trigger and the popup, in px.
  offset?: number;
  // Flip to the other side when there is no room. Callers that compute
  // their own above/below placement turn it off.
  flip?: boolean;
  // Focus the first item on open ("menu" only), like MUI Menu.
  autoFocusItem?: boolean;
  // Write aria-expanded / aria-controls on the trigger while open (for
  // triggers that do not render them themselves).
  wireAnchorAria?: boolean;
  id?: string;
  PaperProps?: AnchoredMenuPaperProps;
  MenuListProps?: Partial<MenuListProps>;
  zIndex?: number;
};

export function AnchoredMenu({
  anchorEl,
  open,
  onClose,
  children,
  variant = "menu",
  placement = "bottom-start",
  offset = 0,
  flip = true,
  autoFocusItem = true,
  wireAnchorAria = false,
  id,
  PaperProps,
  MenuListProps,
  zIndex = 1300,
}: AnchoredMenuProps) {
  const generatedId = useId();
  const menuId = id ?? `anchored-menu-${generatedId.replace(/:/g, "")}`;
  const isOpen = open && Boolean(anchorEl);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useCloseWhenAnchorLeavesView(anchorEl, isOpen, onClose);

  const closeAndFocusAnchor = useCallback(() => {
    onCloseRef.current();
    anchorEl?.focus({ preventScroll: true });
  }, [anchorEl]);

  // A click on the trigger while the popup is open closes it (toggle). The
  // native listener runs before React's root listener; stopping it there
  // keeps the trigger's own onClick from opening the popup again.
  useEffect(() => {
    if (!isOpen || !anchorEl) return undefined;
    const onAnchorClick = (event: MouseEvent) => {
      event.stopPropagation();
      onCloseRef.current();
    };
    const timer = window.setTimeout(
      () => anchorEl.addEventListener("click", onAnchorClick),
      0,
    );
    return () => {
      window.clearTimeout(timer);
      anchorEl.removeEventListener("click", onAnchorClick);
    };
  }, [anchorEl, isOpen]);

  useEffect(() => {
    if (!wireAnchorAria || !anchorEl || !isOpen) return undefined;
    anchorEl.setAttribute("aria-expanded", "true");
    anchorEl.setAttribute("aria-controls", menuId);
    // Callers clear anchorEl on close, so the reset runs in the cleanup.
    return () => {
      anchorEl.setAttribute("aria-expanded", "false");
      anchorEl.removeAttribute("aria-controls");
    };
  }, [anchorEl, isOpen, menuId, wireAnchorAria]);

  const container = useMemo(() => getModalFocusRoot(anchorEl), [anchorEl]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    // A nested control (for example an open calendar) that handled Esc
    // itself keeps the popup open.
    if (event.key !== "Escape" || event.defaultPrevented) return;
    // Stop here so an enclosing Dialog does not also close.
    event.preventDefault();
    event.stopPropagation();
    closeAndFocusAnchor();
  };
  const handleKeyDownCapture = (event: React.KeyboardEvent<HTMLElement>) => {
    if (variant === "menu" && event.key === "Tab") {
      event.preventDefault();
      closeAndFocusAnchor();
    }
  };

  const { sx: paperSx, ...paperRest } = PaperProps ?? {};

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement={placement}
      container={container}
      popperOptions={{ strategy: "fixed" }}
      modifiers={[
        { name: "offset", options: { offset: [0, offset] } },
        { name: "flip", enabled: flip, options: { padding: 8 } },
        { name: "preventOverflow", options: { padding: 8 } },
      ]}
      sx={{ zIndex }}
      data-anchored-menu=""
    >
      <ClickAwayListener
        mouseEvent="onMouseDown"
        onClickAway={(event) => {
          if (anchorEl?.contains(event.target as Node)) return;
          onCloseRef.current();
        }}
      >
        <Paper
          elevation={8}
          {...paperRest}
          role={variant === "panel" ? paperRest.role ?? "dialog" : paperRest.role}
          id={variant === "panel" ? menuId : paperRest.id}
          sx={[defaultPaperSx, ...toSxArray(paperSx)] as SxProps<Theme>}
          onKeyDown={handleKeyDown}
          onKeyDownCapture={handleKeyDownCapture}
        >
          {variant === "menu" ? (
            <MenuList
              id={menuId}
              autoFocusItem={isOpen && autoFocusItem}
              {...MenuListProps}
            >
              {children}
            </MenuList>
          ) : (
            children
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}

// TextField `select` / MUI Select menus. The menu stays a MUI Popover, but it
// no longer blocks the page: no backdrop, no scroll lock, and the full-screen
// root lets wheel and pointer events pass through to the page. The menu
// closes on an outside press, on a page scroll and on resize (a Popover does
// not follow its trigger). Usage:
//   const pageSizeSelect = useNonModalSelect();
//   <TextField select SelectProps={{ ...pageSizeSelect.selectProps, ... }} />
export const NON_MODAL_SELECT_PAPER_ATTRIBUTE = "data-non-modal-select-paper";

const isInsideSelectPaper = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(target.closest(`[${NON_MODAL_SELECT_PAPER_ATTRIBUTE}]`));

export const getNonModalSelectMenuProps = (
  menuProps?: Partial<MenuProps>,
): Partial<MenuProps> => {
  const paperProps = (menuProps?.PaperProps ?? {}) as AnchoredMenuPaperProps;
  return {
    ...menuProps,
    disableScrollLock: true,
    hideBackdrop: true,
    disableEnforceFocus: true,
    // useNonModalSelect returns focus to the trigger without scrolling it
    // into view, so a page-size change can move the page to the table top.
    disableRestoreFocus: true,
    sx: [{ pointerEvents: "none" }, ...toSxArray(menuProps?.sx)] as SxProps<Theme>,
    PaperProps: {
      ...paperProps,
      [NON_MODAL_SELECT_PAPER_ATTRIBUTE]: "true",
      sx: [{ pointerEvents: "auto" }, ...toSxArray(paperProps.sx)] as SxProps<Theme>,
    } as MenuProps["PaperProps"],
  };
};

export const useNonModalSelect = (menuProps?: Partial<MenuProps>) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onOutsidePress = (event: Event) => {
      if (!isInsideSelectPaper(event.target)) close();
    };
    const onScroll = (event: Event) => {
      if (!isInsideSelectPaper(event.target)) close();
    };
    // Armed after the opening press so that press does not close the menu.
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onOutsidePress);
      document.addEventListener("touchstart", onOutsidePress);
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", close);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onOutsidePress);
      document.removeEventListener("touchstart", onOutsidePress);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return {
    open,
    selectProps: {
      open,
      onOpen: (event: React.SyntheticEvent) => {
        triggerRef.current =
          event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
        setOpen(true);
      },
      // MUI closes on an item choice, Esc and Tab: focus goes back to the
      // trigger. An outside press or a scroll closes without moving focus.
      onClose: () => {
        setOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      },
      MenuProps: getNonModalSelectMenuProps(menuProps),
    },
  };
};
